import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, LogOut, Instagram, Pencil, Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import PromoBanner from "../components/PromoBanner";
import { useT } from "../lib/i18n";
import { api, photoUrl, qrUrl } from "../lib/api";

const VIP_TEX = "https://static.prod-images.emergentagent.com/jobs/878a64be-86ab-4267-879d-add4365a5f54/images/5b28ae37caa1e8d81fe15bc3df4f468d80309035611067ed3e80402ee94f143a.png";

export default function MemberVip() {
    const { t } = useT();
    const navigate = useNavigate();
    const [me, setMe] = useState(null);
    const [qr, setQr] = useState("");
    const [showEdit, setShowEdit] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const refresh = async () => {
        const { data } = await api.get("/member/me");
        setMe(data);
        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        return data;
    };

    useEffect(() => {
        const token = localStorage.getItem("sc_member_token");
        if (!token) {
            navigate("/member", { replace: true });
            return;
        }
        (async () => {
            try {
                await refresh();
                const res = await fetch(qrUrl(), { headers: { Authorization: `Bearer ${token}` } });
                const blob = await res.blob();
                setQr(URL.createObjectURL(blob));
            } catch {
                localStorage.removeItem("sc_member_token");
                navigate("/member", { replace: true });
            }
        })();
    }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    const signout = () => {
        localStorage.removeItem("sc_member_token");
        localStorage.removeItem("sc_member_id");
        navigate("/", { replace: true });
    };

    const saveProfile = async () => {
        setSaving(true);
        try {
            await api.patch("/member/me", { first_name: firstName, last_name: lastName });
            await refresh();
            toast.success(t("profile.saved"));
            setShowEdit(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const uploadPhoto = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Image only"); return; }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("photo", file);
            await api.post("/member/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await refresh();
            toast.success(t("profile.saved"));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    if (!me) {
        return (
            <div className="min-h-[100dvh] bg-bg-primary flex items-center justify-center text-ink-muted text-sm tracking-[0.3em] uppercase">
                Loading…
            </div>
        );
    }

    const isVip = me.status === "vip" || me.vip;
    const joined = me.created_at ? new Date(me.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—";

    return (
        <div className="min-h-[100dvh] text-ink-primary relative" data-testid="member-vip-page">
            <Header />
            <PromoBanner />
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(80%_50%_at_50%_0%,rgba(212,175,55,0.10),transparent_70%)]" />
            </div>

            <main className="relative max-w-md mx-auto px-6 pt-32 pb-16">
                <div className="flex items-center justify-between mb-8">
                    <div className="label-eyebrow">{t("vip.eyebrow")}</div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowEdit(true)}
                            data-testid="member-edit"
                            className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <Pencil size={14} />
                            {t("profile.edit")}
                        </button>
                        <button
                            onClick={signout}
                            data-testid="member-signout"
                            className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-ink-secondary hover:text-gold"
                        >
                            <LogOut size={14} />
                            {t("vip.signout")}
                        </button>
                    </div>
                </div>

                {/* VIP Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20, rotateX: -8 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="relative vip-card-bg rounded-md overflow-hidden border border-gold/30 p-6 shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
                    data-testid="vip-card"
                >
                    {/* texture overlay */}
                    <div
                        className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
                        style={{ backgroundImage: `url(${VIP_TEX})`, backgroundSize: "cover", backgroundPosition: "center" }}
                    />

                    <div className="relative flex items-start justify-between">
                        <div>
                            <div className="label-eyebrow">{isVip ? t("vip.status_vip") : t("vip.status_approved")}</div>
                            <div className="font-serif text-2xl mt-1 text-ink-primary">ScottsClub</div>
                        </div>
                        {isVip && (
                            <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center text-gold">
                                <Crown size={18} strokeWidth={1.4} />
                            </div>
                        )}
                    </div>

                    {/* Photo + name */}
                    <div className="relative mt-6 flex items-center gap-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden border border-gold/40 bg-bg-secondary flex-shrink-0">
                            {me.photo_id && (
                                <img src={photoUrl(me.photo_id)} alt={me.full_name} className="w-full h-full object-cover" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="font-serif text-2xl gold-text leading-tight truncate" data-testid="member-name">
                                {me.full_name}
                            </div>
                            <div className="text-ink-secondary text-sm flex items-center gap-1.5 mt-1">
                                <Instagram size={12} /> @{me.instagram}
                            </div>
                        </div>
                    </div>

                    {/* QR */}
                    <div className="relative mt-8 rounded-sm bg-white/95 p-5 flex items-center justify-center" data-testid="member-qr">
                        {qr ? <img src={qr} alt="member QR" className="w-56 h-56" /> : <div className="w-56 h-56 animate-pulse bg-neutral-200" />}
                    </div>
                    <p className="relative mt-4 text-center text-ink-muted text-xs tracking-[0.2em] uppercase">
                        {t("vip.tagline")}
                    </p>

                    {/* Footer stats */}
                    <div className="relative mt-6 pt-6 border-t border-gold/15 grid grid-cols-2 gap-4 text-sm">
                        <div>
                            <div className="text-ink-muted text-[10px] tracking-[0.28em] uppercase">{t("vip.check_ins")}</div>
                            <div className="font-serif text-2xl text-ink-primary mt-1" data-testid="member-checkins">{me.check_ins ?? 0}</div>
                        </div>
                        <div>
                            <div className="text-ink-muted text-[10px] tracking-[0.28em] uppercase">{t("vip.joined")}</div>
                            <div className="font-serif text-2xl text-ink-primary mt-1">{joined}</div>
                        </div>
                    </div>
                </motion.div>
            </main>

            {/* Edit profile modal */}
            <AnimatePresence>
                {showEdit && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={() => setShowEdit(false)}>
                        <motion.div
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md p-8 rounded-sm glass-strong gold-border-anim"
                            data-testid="member-edit-modal"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="font-serif text-2xl">{t("profile.edit")}</h3>
                                <button onClick={() => setShowEdit(false)} className="text-ink-secondary hover:text-gold"><X size={18} /></button>
                            </div>

                            {/* Photo replace */}
                            <div className="flex items-center gap-4">
                                <div className="w-20 h-20 rounded-full overflow-hidden border border-gold/40 bg-bg-secondary">
                                    {me.photo_id && <img src={photoUrl(me.photo_id) + `?t=${me.updated_at || ""}`} alt="" className="w-full h-full object-cover" />}
                                </div>
                                <div>
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        data-testid="member-photo-input"
                                        onChange={(e) => uploadPhoto(e.target.files?.[0])}
                                    />
                                    <button
                                        onClick={() => fileRef.current?.click()}
                                        disabled={uploading}
                                        data-testid="member-change-photo"
                                        className="inline-flex items-center gap-2 px-3 py-2 border border-gold/40 text-gold rounded-sm hover:bg-gold/10 text-xs tracking-[0.22em] uppercase disabled:opacity-50"
                                    >
                                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                                        {uploading ? t("profile.uploading") : t("profile.change_photo")}
                                    </button>
                                </div>
                            </div>

                            <div className="mt-6 space-y-4">
                                <div>
                                    <label className="label-eyebrow block mb-2">{t("profile.first_name")}</label>
                                    <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="input-line" data-testid="member-first-name" />
                                </div>
                                <div>
                                    <label className="label-eyebrow block mb-2">{t("profile.last_name")}</label>
                                    <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="input-line" data-testid="member-last-name" />
                                </div>
                            </div>

                            <button
                                onClick={saveProfile}
                                disabled={saving}
                                data-testid="member-save-profile"
                                className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-black rounded-sm font-medium hover:bg-gold-bright disabled:opacity-40"
                            >
                                {saving ? <Loader2 size={16} className="animate-spin" /> : t("profile.save")}
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
